import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronDown } from "src/icons/chevron-down";
import { ChevronUp } from "src/icons/chevron-up";
import { LightBulb } from "src/icons/light-bulb";
import {
  getMarkdownConverter,
  setupTurndownService,
} from "src/utils/editorUtils";
import { Link } from "../../editor/extensions/link";

interface InsightsEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  compact?: boolean;
  maxLength?: number;
  showResizeHandle?: boolean;
  onWriteInstructions?: () => void;
  isGeneratingInstructions?: boolean;
  dispatch?: any;
}

export interface InsightsEditorRef {
  focus: () => void;
}

const InsightsEditor = forwardRef<InsightsEditorRef, InsightsEditorProps>(
  (
    {
      value,
      onChange,
      placeholder = "Add your insights...",
      error = false,
      helperText,
      compact = true,
      maxLength = 20000,
      showResizeHandle = false,
      onWriteInstructions,
      isGeneratingInstructions = false,
      dispatch,
    },
    ref
  ) => {
    const [openHeadingsDialog, setOpenHeadingsDialog] = useState(false);
    const [openLinkDialog, setOpenLinkDialog] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");
    const [savedSelection, setSavedSelection] = useState<{
      from: number;
      to: number;
    } | null>(null);
    const linkInputRef = useRef<HTMLInputElement>(null);
    const [editorHeight, setEditorHeight] = useState(compact ? 160 : 280);
    const [isResizing, setIsResizing] = useState(false);
    const [isEditorReady, setIsEditorReady] = useState(false);
    const [isUpdatingFromProp, setIsUpdatingFromProp] = useState(false);
    const editorContainerRef = useRef<HTMLDivElement>(null);
    const resizeStartY = useRef(0);
    const resizeStartHeight = useRef(0);

    // Initialize conversion utilities - memoized to prevent recreation on every render
    const turndownService = useMemo(() => setupTurndownService(), []);
    const markdownConverter = useMemo(() => getMarkdownConverter(), []);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3, 4],
          },
        }),
        Link,
        Table.configure({
          resizable: false,
        }),
        TableRow,
        TableHeader,
        TableCell,
      ],
      content: "",
      onCreate: ({ editor }) => {
        // Editor is fully created and ready
        setIsEditorReady(true);
      },
      onUpdate: useCallback(
        ({ editor }) => {
          // Avoid updating if we're currently updating from prop to prevent loops
          if (isUpdatingFromProp) return;

          const html = editor.getHTML();
          // Convert HTML to Markdown before saving
          const markdown = turndownService.turndown(html);

          // Check character limit before allowing the change
          if (markdown.trim().length <= maxLength) {
            onChange(markdown);
          } else {
            // Show user feedback about character limit
            if (dispatch) {
              dispatch({
                type: 'system/snackAdded',
                payload: {
                  message: `Character limit exceeded (${maxLength.toLocaleString()} characters max)`,
                  type: "warning",
                  duration: 3000,
                },
              });
            }

            // If limit exceeded, revert to the previous valid content
            // Get the current value from props and restore it
            setTimeout(() => {
              const currentHtml = editor.getHTML();
              const currentMarkdown = turndownService.turndown(currentHtml).trim();
              const valueToRestore = value?.trim() || "";

              if (currentMarkdown !== valueToRestore) {
                setIsUpdatingFromProp(true);
                if (valueToRestore) {
                  const htmlToRestore = markdownConverter.makeHtml(valueToRestore);
                  editor.commands.setContent(htmlToRestore, false);
                } else {
                  editor.commands.setContent("<p></p>", false);
                }

                setTimeout(() => {
                  setIsUpdatingFromProp(false);
                }, 0);
              }
            }, 0);
          }
        },
        [isUpdatingFromProp, turndownService, onChange, maxLength, value, markdownConverter, dispatch]
      ),
      editorProps: {
        attributes: {
          "data-placeholder": placeholder,
        },
      },
    });

    // Update editor content when value prop changes (convert Markdown to HTML)
    useEffect(() => {
      if (!editor || !isEditorReady) return;

      const currentHtml = editor.getHTML();
      const currentMarkdown = turndownService.turndown(currentHtml).trim();
      const incomingValue = value?.trim() || "";

      // Only update if the content is actually different
      if (currentMarkdown !== incomingValue) {
        setIsUpdatingFromProp(true);

        if (incomingValue) {
          const newHtml = markdownConverter.makeHtml(incomingValue);
          editor.commands.setContent(newHtml, false); // false = don't emit update
        } else {
          editor.commands.setContent("<p></p>", false);
        }

        // Reset flag after content is set
        setTimeout(() => {
          setIsUpdatingFromProp(false);
        }, 0);
      }
    }, [value, editor, isEditorReady, turndownService, markdownConverter]);

    // Only initialize resize handlers if showResizeHandle is true
    const handleResizeStart = useCallback(
      (e: React.MouseEvent) => {
        if (!showResizeHandle) return;
        e.preventDefault();
        setIsResizing(true);
        resizeStartY.current = e.clientY;
        resizeStartHeight.current = editorHeight;
      },
      [editorHeight, showResizeHandle]
    );

    const handleResizeMove = useCallback(
      (e: MouseEvent) => {
        if (!isResizing || !showResizeHandle) return;
        e.preventDefault();
        const deltaY = e.clientY - resizeStartY.current;
        const newHeight = Math.max(
          160,
          Math.min(
            Math.floor(window.innerHeight * 0.8),
            resizeStartHeight.current + deltaY
          )
        );
        setEditorHeight(newHeight);
      },
      [isResizing, showResizeHandle]
    );

    const handleResizeEnd = useCallback(() => {
      setIsResizing(false);
    }, []);

    useEffect(() => {
      if (isResizing && showResizeHandle) {
        document.addEventListener("mousemove", handleResizeMove);
        document.addEventListener("mouseup", handleResizeEnd);
        document.body.style.cursor = "row-resize";
        document.body.style.userSelect = "none";

        return () => {
          document.removeEventListener("mousemove", handleResizeMove);
          document.removeEventListener("mouseup", handleResizeEnd);
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
        };
      }
    }, [isResizing, handleResizeMove, handleResizeEnd, showResizeHandle]);

    // Removed conflicting focus useEffect - let MUI Dialog handle focus naturally

    // Expose focus method via ref
    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          if (editor && isEditorReady) {
            // Try multiple focus methods for better reliability
            setTimeout(() => {
              editor.commands.focus();
              // Fallback: try direct DOM focus if TipTap focus fails
              setTimeout(() => {
                try {
                  const editorElement = editor.view.dom;
                  if (
                    editorElement &&
                    document.activeElement !== editorElement
                  ) {
                    editorElement.focus();
                    // Ultimate fallback: focus the contenteditable directly
                    const editableElement = editorElement.querySelector(
                      '[contenteditable="true"]'
                    );
                    if (
                      editableElement &&
                      document.activeElement !== editableElement
                    ) {
                      (editableElement as HTMLElement).focus();
                    }
                  }
                } catch (error) {
                  console.warn("Editor focus fallback failed:", error);
                }
              }, 10);
            }, 50);
          } else if (!isEditorReady) {
            // If editor not ready, retry after a short delay
            setTimeout(() => {
              if (editor && isEditorReady) {
                editor.commands.focus();
                // Fallback: try direct DOM focus if TipTap focus fails
                setTimeout(() => {
                  try {
                    const editorElement = editor.view.dom;
                    if (
                      editorElement &&
                      document.activeElement !== editorElement
                    ) {
                      editorElement.focus();
                      const editableElement = editorElement.querySelector(
                        '[contenteditable="true"]'
                      );
                      if (
                        editableElement &&
                        document.activeElement !== editableElement
                      ) {
                        (editableElement as HTMLElement).focus();
                      }
                    }
                  } catch (error) {
                    console.warn("Editor focus retry fallback failed:", error);
                  }
                }, 10);
              }
            }, 100);
          }
        },
      }),
      [editor, isEditorReady]
    );

    if (!editor) return null;

    const getCharacterCount = () => {
      const html = editor.getHTML();
      const markdown = turndownService.turndown(html);
      // Count characters in the Markdown representation (what gets saved)
      return markdown.trim().length;
    };

    const getCurrentHeading = () => {
      if (editor.isActive("heading", { level: 1 })) return "H1";
      if (editor.isActive("heading", { level: 2 })) return "H2";
      if (editor.isActive("heading", { level: 3 })) return "H3";
      if (editor.isActive("heading", { level: 4 })) return "H4";
      return "P";
    };

    if (!editor) return null;

    return (
      <Box>
        {/* Integrated Editor Container */}
        <Box
          ref={editorContainerRef}
          sx={{
            border: error ? "1px solid #d32f2f" : "1px solid #E6E8F0",
            borderRadius: "8px",
            height: `${editorHeight}px`,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {/* Toolbar inside editor container */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              p: 1,
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #E6E8F0",
              flexShrink: 0,
            }}
          >
            {/* Headings Dropdown */}
            <Box sx={{ position: "relative" }}>
              <Button
                size="small"
                onClick={() => setOpenHeadingsDialog(!openHeadingsDialog)}
                sx={{
                  color: "#333333",
                  fontSize: "12px",
                  minWidth: "36px",
                  height: "24px",
                  padding: "2px 6px",
                }}
              >
                {getCurrentHeading()}
                {openHeadingsDialog ? <ChevronUp /> : <ChevronDown />}
              </Button>

              {openHeadingsDialog && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    backgroundColor: "white",
                    borderRadius: "4px",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                    zIndex: 1000,
                    minWidth: "120px",
                    border: "1px solid #E6E8F0",
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => {
                      editor.chain().setParagraph().run();
                      setOpenHeadingsDialog(false);
                    }}
                    sx={{ color: "#333333", fontSize: "12px", width: "100%" }}
                  >
                    Paragraph
                  </Button>
                  {[1, 2, 3, 4].map((level) => (
                    <Button
                      key={level}
                      size="small"
                      onClick={() => {
                        editor
                          .chain()
                          .toggleHeading({ level: level as 1 | 2 | 3 | 4 })
                          .run();
                        setOpenHeadingsDialog(false);
                      }}
                      sx={{ color: "#333333", fontSize: "12px", width: "100%" }}
                    >
                      Heading {level}
                    </Button>
                  ))}
                </Box>
              )}
            </Box>

            {/* Bold */}
            <Button
              size="small"
              onClick={() => {
                editor.chain().toggleBold().run();
              }}
              sx={{
                color: editor.isActive("bold") ? "#5048E5" : "#333333",
                minWidth: "24px",
                height: "24px",
                padding: "2px",
              }}
            >
              <FormatBoldIcon fontSize="small" />
            </Button>

            {/* Bullet List */}
            <Button
              size="small"
              onClick={() => {
                editor.chain().toggleBulletList().run();
              }}
              sx={{
                color: editor.isActive("bulletList") ? "#5048E5" : "#333333",
                minWidth: "24px",
                height: "24px",
                padding: "2px",
              }}
            >
              <FormatListBulletedIcon fontSize="small" />
            </Button>

            {/* Numbered List */}
            <Button
              size="small"
              onClick={() => {
                editor.chain().toggleOrderedList().run();
              }}
              sx={{
                color: editor.isActive("orderedList") ? "#5048E5" : "#333333",
                minWidth: "24px",
                height: "24px",
                padding: "2px",
              }}
            >
              <FormatListNumberedIcon fontSize="small" />
            </Button>

            {/* Link */}
            <Button
              size="small"
              onClick={() => {
                const { from, to } = editor.state.selection;
                setSavedSelection({ from, to });
                setOpenLinkDialog(true);
              }}
              sx={{
                color: editor.isActive("link") ? "#5048E5" : "#333333",
                minWidth: "24px",
                height: "24px",
                padding: "2px",
              }}
            >
              <LinkIcon fontSize="small" />
            </Button>

            {/* Clear Formatting */}
            <Button
              size="small"
              onClick={() => {
                editor.chain().unsetAllMarks().run();
              }}
              sx={{
                color: "#333333",
                minWidth: "24px",
                height: "24px",
                padding: "2px",
              }}
            >
              <FormatClearIcon fontSize="small" />
            </Button>

            {/* Write Instructions Button */}
            {onWriteInstructions && (
              <Box
                sx={{
                  ml: "auto",
                  pl: 1,
                }}
              >
                <LoadingButton
                  size="small"
                  onClick={onWriteInstructions}
                  loading={isGeneratingInstructions}
                  disabled={isGeneratingInstructions}
                  sx={{
                    color: "#5048E5",
                    fontSize: "12px",
                    fontWeight: 600,
                    minWidth: "auto",
                    height: "24px",
                    padding: "2px 8px",
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <LightBulb sx={{ fontSize: "14px" }} />
                  {isGeneratingInstructions
                    ? "Writing..."
                    : "Write instructions"}
                </LoadingButton>
              </Box>
            )}
          </Box>

          {/* Editor Content */}
          <Box
            onClick={(e) => {
              // Only focus if the click came from the content area, not from toolbar buttons
              if (editor && e.target === e.currentTarget) {
                editor.commands.focus();
              }
            }}
            sx={{
              flex: 1,
              overflowY: "auto",
              position: "relative",
              cursor: "text",
              scrollBehavior: "smooth",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "3px",
                "&:hover": {
                  background: "#555",
                },
              },
              "& .ProseMirror": {
                outline: "none",
                padding: "12px",
                minHeight: "100%",
                fontSize: "14px",
                fontFamily: "Inter",
                scrollBehavior: "smooth",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                "& p": {
                  margin: "0 0 8px 0",
                  lineHeight: 1.5,
                  wordWrap: "break-word",
                },
                "& h1, & h2, & h3, & h4": {
                  margin: "8px 0",
                  fontWeight: "600",
                  wordWrap: "break-word",
                },
                "& ul, & ol": {
                  paddingLeft: "20px",
                  margin: "8px 0",
                  "& li": {
                    wordWrap: "break-word",
                  },
                },
                "& a": {
                  color: "#5048E5",
                  textDecoration: "underline",
                  wordWrap: "break-word",
                },
                "& table": {
                  borderCollapse: "collapse",
                  margin: "8px 0",
                  overflow: "hidden",
                  tableLayout: "fixed",
                  width: "100%",
                  border: "1px solid #E6E8F0",
                },
                "& td, & th": {
                  border: "1px solid #E6E8F0",
                  boxSizing: "border-box",
                  minWidth: "1em",
                  padding: "6px 8px",
                  position: "relative",
                  verticalAlign: "top",
                },
                "& th": {
                  backgroundColor: "#f8f9fa",
                  fontWeight: "bold",
                  textAlign: "left",
                },
                "& td p, & th p": {
                  margin: "0 !important",
                },
              },
              "& .ProseMirror p.is-editor-empty:first-child::before": {
                color: "#adb5bd",
                content: "attr(data-placeholder)",
                float: "left",
                height: 0,
                pointerEvents: "none",
              },
            }}
          >
            <EditorContent editor={editor} />
          </Box>

          {/* Resize Handle - Only show when showResizeHandle is true */}
          {showResizeHandle && (
            <Box
              onMouseDown={handleResizeStart}
              sx={{
                position: "absolute",
                bottom: 0,
                right: 5,
                width: "16px",
                height: "16px",
                cursor: "nw-resize",
                backgroundColor: "transparent",
                zIndex: 10,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "2px",
                  right: "2px",
                  width: "0",
                  height: "0",
                  borderLeft: "8px solid transparent",
                  borderBottom: "8px solid #ccc",
                  borderRight: "8px solid #ccc",
                  borderTop: "8px solid transparent",
                },
                "&:hover::after": {
                  borderBottomColor: "#999",
                  borderRightColor: "#999",
                },
              }}
            />
          )}
        </Box>

        {/* Character Count and Helper Text Container */}
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography
            sx={{
              fontSize: "12px",
              color: error ? "#d32f2f" : "#7B85AD",
              textAlign: "right",
            }}
          >
            {getCharacterCount()}/{maxLength}
          </Typography>

          {helperText && (
            <Typography
              sx={{
                fontSize: "12px",
                color: "#d32f2f",
                mt: 0.5,
              }}
            >
              {helperText}
            </Typography>
          )}
        </Box>

        {/* Link Dialog */}
        <Dialog
          open={openLinkDialog}
          onClose={() => setOpenLinkDialog(false)}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          TransitionProps={{
            onEntered: () => {
              // Focus the input after dialog transition completes
              setTimeout(() => {
                linkInputRef.current?.focus();
              }, 50);
            },
          }}
        >
          <DialogTitle>Add Link</DialogTitle>
          <DialogContent>
            <TextField
              inputRef={linkInputRef}
              margin="dense"
              label="URL"
              type="url"
              fullWidth
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onPaste={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Ensure focus when clicked
                linkInputRef.current?.focus();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenLinkDialog(false);
                setSavedSelection(null);
                setLinkUrl("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (linkUrl && savedSelection) {
                  editor
                    .chain()
                    .setTextSelection(savedSelection)
                    .setLink({ href: linkUrl })
                    .run();

                  // No need to restore selection - TipTap handles it naturally
                }
                setLinkUrl("");
                setOpenLinkDialog(false);
                setSavedSelection(null);
              }}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);

InsightsEditor.displayName = "InsightsEditor";

export default InsightsEditor;
